// ============================================================
// SpringBootValidation.jsx
//
// A branded, responsive course content page for the
// "Validation in Spring Boot" lesson.
//
// Author: ProDev Enterprise Series (Java Branch)
// Features: Jakarta Bean Validation, custom constraints,
//           and global exception handling patterns.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import RuleIcon              from "@mui/icons-material/Rule";
import VerifiedUserIcon      from "@mui/icons-material/VerifiedUser";
import NewReleasesIcon       from "@mui/icons-material/NewReleases";
import TerminalIcon          from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import CodeIcon               from "@mui/icons-material/Code";
import ErrorOutlineIcon       from "@mui/icons-material/ErrorOutline";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ title, code }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Typography sx={{ color: BRAND.gold, fontSize: 11, fontWeight: 700, fontFamily: "monospace", textTransform: 'uppercase' }}>{title}</Typography>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5 }}>
                <Typography component="pre" sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.codeText, whiteSpace: "pre-wrap", m: 0 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function SpringBootValidation() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (window.pageYOffset > 400) setShowScroll(true);
            else setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Java Enterprise · Module 07"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Data <span>Validation</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Secure your application by enforcing strict input contracts. Using Jakarta Bean Validation (Hibernate Validator), Spring Boot allows you to declaratively validate DTOs and handle errors gracefully before they reach your business logic.
                    </Typography>
                </Box>

                {/* 1. TRIGGERING VALIDATION */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<VerifiedUserIcon />} label="1. The @Valid Annotation" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Place <code style={{color: BRAND.white}}>@Valid</code> on your controller arguments to trigger the validation engine. If the object fails, Spring throws a <strong>MethodArgumentNotValidException</strong>.
                    </Typography>
                    <CodeBlock
                        title="Controller Integration"
                        code={`@PostMapping("/users")\npublic ResponseEntity<User> register(@Valid @RequestBody UserDTO dto) {\n    return ResponseEntity.ok(userService.create(dto));\n}`}
                    />
                </Box>

                {/* 2. STANDARD CONSTRAINTS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<RuleIcon />} label="2. Built-in Constraints" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Apply these standard annotations directly to your DTO fields to define the "shape" of valid data.
                    </Typography>
                    <CodeBlock
                        title="UserDTO.java"
                        code={`@NotBlank(message = "Username is required")\n@Size(min = 3, max = 15)\nprivate String username;\n\n@Email(message = "Invalid format")\nprivate String email;\n\n@Min(18) @Max(99)\nprivate int age;`}
                    />
                </Box>

                {/* 3. CUSTOM VALIDATORS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<NewReleasesIcon />} label="3. Domain-Specific Validators" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        For logic that standard annotations can't handle, create a custom constraint by implementing <code style={{color: BRAND.white}}>ConstraintValidator</code>.
                    </Typography>
                    <CodeBlock
                        title="Custom Role Validator"
                        code={`public class RoleValidator implements ConstraintValidator<ValidRole, String> {\n    @Override\n    public boolean isValid(String value, ConstraintValidatorContext ctx) {\n        return List.of("ADMIN", "USER").contains(value);\n    }\n}`}
                    />
                </Box>

                {/* 4. EXCEPTION HANDLING */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<ErrorOutlineIcon />} label="4. Formatting Error Responses" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Use a <code style={{color: BRAND.white}}>@RestControllerAdvice</code> to catch validation errors globally and return a clean, user-friendly JSON map.
                    </Typography>
                    <CodeBlock
                        title="GlobalExceptionHandler.java"
                        code={`@ExceptionHandler(MethodArgumentNotValidException.class)\npublic ResponseEntity<Map<String, String>> handleErrors(MethodArgumentNotValidException ex) {\n    Map<String, String> errors = new HashMap<>();\n    ex.getBindingResult().getFieldErrors().forEach(f -> \n        errors.put(f.getField(), f.getDefaultMessage())\n    );\n    return ResponseEntity.badRequest().body(errors);\n}`}
                    />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* HIGHLIGHTS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Validation Highlights" />
                    <Grid container spacing={1.5}>
                        {[
                            "Enforce @NotBlank to prevent empty or whitespace strings",
                            "Use @Validated for group-based or service-layer validation",
                            "Isolate validation logic in DTOs to keep Entities clean",
                            "Apply @Pattern for complex regex-based string matching",
                            "Return 400 Bad Request automatically on validation failure",
                            "Centralize error formatting in a Global Exception Handler"
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/java-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Return to Hub
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Prefer using <code>@NotBlank</code> over <code>@NotNull</code> for string fields. <code>@NotBlank</code> ensures the string is not null, has a length greater than zero, and contains at least one non-whitespace character.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
